import {AxiosInstance, AxiosResponse} from "axios";
import {AxiosUtil} from "@app/utils/AxiosUtil";
import {AuthUtil} from "@app/utils/AuthUtil";
import {FingerprintUtil} from "@app/utils/FingerprintUtil";
import {TokenRefreshLock} from "@app/utils/TokenRefreshLock";
import UserInterface from "@dashart/dashart-gpt-shared-library/dist/interfaces/UserInterface";

export class AuthService {

    private static rawAxiosInstance: AxiosInstance;

    private static getRawAxiosInstance(): AxiosInstance {
        if (!this.rawAxiosInstance) {
            this.rawAxiosInstance = AxiosUtil.createNonConfiguredAxiosInstance();
        }

        return this.rawAxiosInstance;
    }

    /**
     * Log user in
     * Sends browser fingerprint for session binding
     */
    static async login(userName: string, password: string): Promise<UserInterface> {
        const fingerprintHash = await FingerprintUtil.getFingerprint();

        return this.getRawAxiosInstance().post(
            '/v1/auth/login',
            {
                "email": userName,
                "password": password,
                "fingerprintHash": fingerprintHash
            }
        ).then(
            (response: AxiosResponse) => {
                if (response.status === 200) {
                    // Store access token (JWT - can be decoded)
                    AuthUtil.setAccessToken(response.data?.accessToken ?? '');
                    AuthUtil.setDeviceId(response.data?.deviceId ?? 'web');

                    // Store opaque refresh token with server-provided expiry
                    const refreshToken = response.data?.refreshToken ?? '';
                    const refreshTokenExpiresAt = response.data?.refreshTokenExpiresAt ?? 0;
                    AuthUtil.setRefreshTokenWithExpiry(refreshToken, refreshTokenExpiresAt);

                    // Extract user from access token (still JWT)
                    const tokenPayload = AuthUtil.getTokenPayload(response.data?.accessToken ?? '');
                    return Promise.resolve(<UserInterface>tokenPayload?.user)
                } else {
                    return Promise.reject(response)
                }
            },
            (error) => {
                console.log('login failed', error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };

    /**
     * Validate if session is still valid and get new refresh token
     * Includes fingerprint for server-side validation
     */
    static async checkIfSessionValidAndGetNewRefreshToken(): Promise<UserInterface | null> {
        const fingerprintHash = await FingerprintUtil.getFingerprint();

        // Single-flight across tabs: the refresh token is read INSIDE the lock,
        // so a tab that waited for another tab's rotation uses the rotated token.
        return TokenRefreshLock.withLock(() => this.getRawAxiosInstance().post(
            '/v1/auth/refresh-session',
            {
                refreshToken: AuthUtil.getRefreshToken(),
                fingerprintHash: fingerprintHash,
            },
        )).then(
            (response: AxiosResponse) => {
                if (response.status === 200) {
                    let accessToken = '';

                    if (response.data?.token) {
                        accessToken = response.data?.token?.accessToken ?? '';
                        AuthUtil.setAccessToken(accessToken);

                        // Store rotated refresh token with expiry
                        if (response.data?.token?.refreshToken) {
                            const refreshTokenExpiresAt = response.data?.token?.refreshTokenExpiresAt ?? 0;
                            AuthUtil.setRefreshTokenWithExpiry(
                                response.data.token.refreshToken,
                                refreshTokenExpiresAt,
                            );
                        }
                    } else {
                        return Promise.reject(null)
                    }

                    if (!accessToken) {
                        return Promise.reject(null)
                    }

                    const tokenPayload = AuthUtil.getTokenPayload(accessToken)
                    return Promise.resolve(tokenPayload?.user ?? null)
                } else {
                    return Promise.reject(null)
                }
            },
            (error) => {
                console.log('session not valid', error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };

    /**
     * Get new access token using refresh token
     * Rotation: server returns new access token + new refresh token
     */
    static async requestNewAccessToken(refreshToken: any): Promise<any | null> {
        const fingerprintHash = await FingerprintUtil.getFingerprint();

        return this.getRawAxiosInstance().post(
            '/v1/auth/refresh-access-token',
            {
                refreshToken: refreshToken,
                fingerprintHash: fingerprintHash,
            }
        ).then(
            (response: AxiosResponse) => {
                if (response.status === 200) {
                    const tokenData = response.data?.token;
                    if (tokenData) {
                        // Store new access token
                        AuthUtil.setAccessToken(tokenData.accessToken ?? '');

                        // Store rotated refresh token with expiry
                        if (tokenData.refreshToken) {
                            AuthUtil.setRefreshTokenWithExpiry(
                                tokenData.refreshToken,
                                tokenData.refreshTokenExpiresAt ?? 0,
                            );
                        }

                        return Promise.resolve(tokenData.accessToken ?? null)
                    }

                    return Promise.reject(null)
                } else {
                    return Promise.reject(null)
                }
            },
            (error) => {
                console.log('session not valid', error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };

    /**
     * Log user out
     * Sends refresh token for server-side revocation
     * No longer requires JwtAuthGuard (uses raw axios)
     */
    static async logout(): Promise<boolean> {
        try {
            const refreshToken = AuthUtil.getRefreshToken();
            await this.getRawAxiosInstance().post(
                '/v1/auth/logout',
                {
                    refreshToken: refreshToken,
                }
            );
        } catch (error) {
            console.log('logout failed (continuing cleanup)', error);
        }

        // Always clear local tokens regardless of server response
        AuthUtil.clearTokens();

        return true;
    };
}
